/**
 * Wraps one JPEG in a single-page PDF.
 *
 * No PDF library: a page holding one image is a handful of objects, and the
 * browser already has the JPEG encoder. Kept free of DOM calls so it can be
 * exercised outside a browser.
 *
 * The page is sized like the paper roll - the image fills the 72 mm print area
 * with a margin around it - and is as tall as the image, so a receipt PDF reads
 * like the printed slip rather than a strip on an A4 sheet.
 */

const POINTS_PER_MM = 72 / 25.4;

export const buildImagePdf = (
  jpeg,
  { pixelWidth, pixelHeight, printWidthMm = 72, marginMm = 4 },
) => {
  const imageWidth = printWidthMm * POINTS_PER_MM;
  const imageHeight = imageWidth * (pixelHeight / pixelWidth);
  const margin = marginMm * POINTS_PER_MM;
  const pageWidth = imageWidth + margin * 2;
  const pageHeight = imageHeight + margin * 2;
  const pt = (value) => value.toFixed(2);

  const encoder = new TextEncoder();
  const chunks = [];
  const offsets = [];
  let length = 0;

  const push = (part) => {
    const bytes = typeof part === "string" ? encoder.encode(part) : part;
    chunks.push(bytes);
    length += bytes.length;
  };
  const object = (n, body) => {
    offsets[n] = length;
    push(`${n} 0 obj\n${body}\nendobj\n`);
  };

  // the second line's high bytes mark the file as binary for transfer tools
  push("%PDF-1.4\n%âãÏÓ\n");
  object(1, "<< /Type /Catalog /Pages 2 0 R >>");
  object(2, "<< /Type /Pages /Kids [3 0 R] /Count 1 >>");
  object(
    3,
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pt(pageWidth)} ${pt(pageHeight)}] ` +
      "/Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>",
  );

  offsets[4] = length;
  push(
    `4 0 obj\n<< /Type /XObject /Subtype /Image /Width ${pixelWidth} /Height ${pixelHeight} ` +
      `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`,
  );
  push(jpeg);
  push("\nendstream\nendobj\n");

  const draw = `q ${pt(imageWidth)} 0 0 ${pt(imageHeight)} ${pt(margin)} ${pt(margin)} cm /Im0 Do Q`;
  object(5, `<< /Length ${draw.length} >>\nstream\n${draw}\nendstream`);

  const xrefAt = length;
  const entries = [1, 2, 3, 4, 5]
    .map((n) => `${String(offsets[n]).padStart(10, "0")} 00000 n \n`)
    .join("");
  push(
    `xref\n0 6\n0000000000 65535 f \n${entries}` +
      `trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xrefAt}\n%%EOF\n`,
  );

  const pdf = new Uint8Array(length);
  let at = 0;
  for (const chunk of chunks) {
    pdf.set(chunk, at);
    at += chunk.length;
  }
  return pdf;
};
